import { useCallback, useState } from 'react';

type IdKey<T> = keyof T;

function useTablePersistentSelection<T extends Record<string, any>>( idKey: IdKey<T> ) {
  const [selectedRows, setSelectedRows] = useState<Map<string, T>>( new Map() );

  const toggleRow = useCallback( ( item: T ) => {
    setSelectedRows( ( prev ) => {
      const rows = new Map( prev );
      const id = item[idKey] as string;
      if ( rows.has( id ) ) rows.delete( id );
      else rows.set( id, item );
      return rows;
    } );
  }, [idKey] );

  const isRowSelected = useCallback( ( id: string ) => selectedRows.has( id ), [selectedRows] );

  const toggleAllRows = useCallback( ( checked: boolean, items: T[] | null = [] ) => {
    if ( !checked ) return setSelectedRows( new Map() );

    const rows = new Map();
    items?.forEach( ( item ) => {
      const id = item[idKey] as string;
      rows.set( id, item );
    } );
    setSelectedRows( rows );
  }, [idKey] );

  const isAllRowsSelected = useCallback(
    ( items: T[] | null = [] ) => {
      const selectedIds = new Set( selectedRows.keys() );
      return !!items?.length
       && items?.every( ( item ) => selectedIds.has( item[idKey] as string ) );
    },
    [selectedRows],
  );

  return {
    selectedRows: Array.from( selectedRows.values() ),
    selectedRowIds: Array.from( selectedRows.keys() ),
    isRowSelected,
    isAllRowsSelected,
    toggleRow,
    toggleAllRows,
  };
}

export default useTablePersistentSelection;
